"use client";
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  ArrowLeft, Upload, Image as ImageIcon, Video, Trash2,
  Plus, Save, X, Package, Tag, DollarSign, Layers,
  Palette, Ruler, Lock, IndianRupee, Boxes, Search,
  ChevronRight, ChevronLeft, Check, Percent,
} from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useProducts }         from '../context/ProductsContext';
import { useCategories }       from '../context/CategoryContext';
import { useSubCategories }    from '../context/SubcategoryContext';
import { useModels }           from '../context/ModelContext';
import { useColors }           from '../context/ColorContext';
import { useProductTypes }     from '../context/ProductTypeContext.jsx';
import { useProductMaterials } from '../context/ProductMaterialContext';
import { useProductShapes }    from '../context/ProductShapeContext.jsx';
import { useSizes }            from '../context/SizesContext';
import { CDN_URL }             from '../config/apiconfig.jsx';

// ── Constants ─────────────────────────────────────────────────────────────────
const MAX_PRODUCT_DETAILS = 5;
const MAX_VARIANTS        = Infinity;
const MAX_BULK_OFFERS     = Infinity;
const STEPS = ['Basic', 'Images', 'Attributes', 'Pricing', 'Variants'];
const FRAME_KEYWORDS = /frame|art|canvas|print|photo|picture|poster/i;

const IMAGE_MAX_SIZE_MB    = 2;
const IMAGE_MAX_SIZE_BYTES = IMAGE_MAX_SIZE_MB * 1024 * 1024;
const ALLOWED_IMAGE_TYPES  = ['image/png', 'image/jpeg', 'image/webp'];
const VIDEO_MAX_SIZE_MB    = 50;
const VIDEO_MAX_SIZE_BYTES = VIDEO_MAX_SIZE_MB * 1024 * 1024;

const CAPITALIZE_FIELDS = new Set([
  'product_name', 'brand_name', 'model_name', 'manufacturer',
  'item_type_name', 'style', 'frame_color', 'frame_material',
  'frame_type', 'size', 'unit_count_type',
]);

const OPTIONAL_FIELDS = [
  'product_type', 'brand_name', 'sku', 'model_name', 'manufacturer',
  'item_type_name', 'material', 'color', 'size', 'item_shape', 'style', 'no_of_items',
  'frame_color', 'frame_material', 'frame_type',
  'max_retail_price', 'sale_price', 'min_seller_allowed_price', 'max_seller_allowed_price',
  'prod_tax_code', 'sale_start_date', 'sale_end_date', 'offering_relase_date',
  'merchant_release_date', 'max_order_quantity', 'min_order_qty', 'offering_message',
  'item_length_longer_edge', 'item_length_unit', 'item_width_shorter_edge', 'item_width_unit',
  'item_depth', 'item_weight', 'item_weight_unit', 'unit_count', 'unit_count_type',
  'item_package_length', 'package_length_unit', 'item_package_width', 'package_width_unit',
  'item_package_height', 'package_height_unit', 'item_package_weight', 'package_weight_unit',
  'return_policy', 'meta_title', 'meta_description', 'slug', 'description',
  'prod_description',
];

const INITIAL_FORM = {
  product_type: '', product_name: '', brand_name: '',
  model_name: '', manufacturer: '', description: '',
  prod_description: '',
  style: '', material: '', no_of_items: '1', item_type_name: '',
  color: '', size: '', item_shape: '',
  frame_color: '', frame_material: '', frame_type: '',
  item_depth: '', unit_count: '', unit_count_type: '',
  item_length_longer_edge: '', item_length_unit: 'cm',
  item_width_shorter_edge: '', item_width_unit: 'cm',
  sku: '', quantity: '', your_price: '',
  max_retail_price: '', min_seller_allowed_price: '',
  max_seller_allowed_price: '', sale_price: '',
  sale_start_date: '', sale_end_date: '',
  offering_relase_date: '', prod_tax_code: '',
  merchant_release_date: '', max_order_quantity: '',
  min_order_qty: '1',
  offering_message: '', wrap_available: false,
  is_customizable: false,
  item_weight: '', item_weight_unit: 'kg',
  item_package_length: '', package_length_unit: 'cm',
  item_package_width: '',  package_width_unit: 'cm',
  item_package_height: '', package_height_unit: 'cm',
  item_package_weight: '', package_weight_unit: 'kg',
  categoryId: '', subCategoryId: '', modalCategoryId: '',
  status: 'Active',
  images: [],
  video: '',
  productDetails: [],
  variants: [],
  bulk_offers: [],
  return_status: 'returnable',
  return_policy: '',
  tags: [],
  meta_title: '', meta_description: '', slug: '',
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const blankDetail = () => ({ title: '', content: '', image: null, preview: null });

const blankVariant = () => ({
  uid:      crypto.randomUUID(),
  id:       null,
  title:    '', image: null, preview: null,
  color:    '', material: '', price: '', size: '',
  shape:    '', quantity: '', sku: '',
});

const blankBulkOffer = () => ({
  uid:            crypto.randomUUID(),
  id:             null,
  min_quantity:   '',
  max_quantity:   '',
  discount_type:  'fixed',
  discount_value: '',
  status:         'Active',
});

// Safe string coercion: null / undefined / "null" → ""
const toStr = (val) => (val == null || String(val) === 'null') ? '' : String(val);

const toSlug = (str) =>
  str.toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

// Returns true if val is a numeric string / number (i.e. looks like a DB id)
const isNumericId = (val) =>
  val != null && val !== '' && /^\d+$/.test(String(val).trim());


const pick = (obj, keys, fallback = '') => {
  for (const k of keys) {
    if (obj?.[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
  }
  return fallback;
};


const resolveLookupId = (idVal, nameVal, list, nameKeys = ['name']) => {
  if (isNumericId(idVal)) return String(idVal);
  const rawName = (nameVal || '').toString().trim().toLowerCase();
  if (rawName && list?.length) {
    const matched = list.find(item =>
      nameKeys.some(k => (item[k] || '').toString().trim().toLowerCase() === rawName)
    );
    if (matched) return String(matched.id);
  }
  return '';
};

const resolveStrictLookupId = (idVal, list) => {
  if (!isNumericId(idVal)) return '';
  const match = list?.find(item => String(item.id) === String(idVal));
  return match ? String(idVal) : '';
};

const resolveShapeId = (v, shapesList) =>
  resolveLookupId(
    pick(v, ['shape_id', 'shape']),
    pick(v, ['shape_name', 'item_shape', 'shape']),
    shapesList,
    ['name']
  );

const parseProductDetails = (description, baseUrl) => {
  if (!description) return [];
  try {
    const parsed = JSON.parse(description);
    if (Array.isArray(parsed)) {
      return parsed.map(d => ({
        title:   d.title   || '',
        content: d.content || '',
        image:   null,
        preview: d.image
          ? d.image.startsWith('http') ? d.image : `${baseUrl}/${d.image}`
          : null,
      }));
    }
  } catch { /* plain-text description, ignore */ }
  return [];
};

const handleNumericWheel   = (e) => { e.target.blur(); };
const handleNumericKeyDown = (e) => {
  if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault();
};
const preventNegativeInput = (value) => {
  const n = parseFloat(value);
  return isNaN(n) || n < 0 ? '' : value;
};

// ── Client-side image / video validation ─────────────────────────────────────
// Runs the moment a file is chosen, before it ever touches state or the
// network, so bad files are rejected immediately with a clear message
// instead of failing later at the backend with a cryptic field name.
const validateImageFile = (file) => {
  if (!file) return null;
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return 'Only PNG, JPG, or WebP images are allowed';
  }
  if (file.size > IMAGE_MAX_SIZE_BYTES) {
    return `Image must be under ${IMAGE_MAX_SIZE_MB} MB (this file is ${(file.size / (1024 * 1024)).toFixed(1)} MB)`;
  }
  return null;
};

const validateVideoFile = (file) => {
  if (!file) return null;
  if (file.type !== 'video/mp4') return 'Only MP4 videos are allowed';
  if (file.size > VIDEO_MAX_SIZE_BYTES) {
    return `Video must be under ${VIDEO_MAX_SIZE_MB} MB (this file is ${(file.size / (1024 * 1024)).toFixed(1)} MB)`;
  }
  return null;
};

// ── Backend error mapping ─────────────────────────────────────────────────
// The backend returns Laravel-style validation errors keyed by the raw
// payload field name, e.g. { "description.0.image": ["The description.0.image
// must not be greater than 2048 kilobytes."] }. Showing that key verbatim is
// confusing. This maps each key onto (a) a friendly label and (b), where
// possible, the SAME internal error key the rest of the form already uses
// (detail_image_0, variant_price_2, bulk_min_1…) so the exact offending
// field is highlighted inline instead of only appearing in a generic banner.
const FIELD_LABELS = {
  product_name: 'Product Name',
  categoryId: 'Category',
  subCategoryId: 'Sub-Category',
  modalCategoryId: 'Modal Category',
  your_price: 'Your Price',
  max_retail_price: 'MRP',
  sale_price: 'Sale Price',
  images: 'Product Images',
  video: 'Product Video',
  return_policy: 'Return Policy',
  min_order_qty: 'Min Order Quantity',
  max_order_quantity: 'Max Order Quantity',
};

const humanizeKey = (key) =>
  FIELD_LABELS[key] || key
    .replace(/[_.]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());

// Normalizes both dot notation ("description.0.image") and bracket
// notation ("description[0][image]") into ['description', '0', 'image'].
const splitBackendKey = (key) =>
  key.replace(/\]/g, '').split(/\.|\[/).filter(Boolean);

const mapBackendErrors = (backendErrors) => {
  const mapped = {};
  const generalMessages = [];

  Object.entries(backendErrors || {}).forEach(([key, msgs]) => {
    const message = Array.isArray(msgs) ? msgs[0] : String(msgs);
    const parts = splitBackendKey(key);

    if (parts[0] === 'description' && parts.length >= 3) {
      const idx = parts[1];
      const sub = parts[2]; // image | title | content
      if (sub === 'image') mapped[`detail_image_${idx}`] = message;
      else generalMessages.push(`Product Detail ${Number(idx) + 1}: ${message}`);
      return;
    }
    if (parts[0] === 'variations' && parts.length >= 3) {
      const idx = parts[1];
      const sub = parts[2];
      if (sub === 'image')       mapped[`variant_image_${idx}`] = message;
      else if (sub === 'price')  mapped[`variant_price_${idx}`] = message;
      else if (sub === 'title')  mapped[`variant_title_${idx}`] = message;
      else generalMessages.push(`Variant ${Number(idx) + 1}: ${message}`);
      return;
    }
    if (parts[0] === 'bulk_offers' && parts.length >= 3) {
      const idx = parts[1];
      const sub = parts[2];
      if (sub === 'min_quantity')        mapped[`bulk_min_${idx}`] = message;
      else if (sub === 'max_quantity')   mapped[`bulk_max_${idx}`] = message;
      else if (sub === 'discount_value') mapped[`bulk_value_${idx}`] = message;
      else generalMessages.push(`Bulk Offer Tier ${Number(idx) + 1}: ${message}`);
      return;
    }

    // Plain top-level field — reuse the field name as the error key so it
    // shows up under the matching input (FieldError already reads these),
    // and also surface a friendly line in the general banner.
    mapped[parts[0]] = message;
    generalMessages.push(`${humanizeKey(parts[0])}: ${message}`);
  });

  if (generalMessages.length) mapped.general = generalMessages.join(' · ');
  return mapped;
};

// ── Component ─────────────────────────────────────────────────────────────────
const AddProductPage = () => {
  const navigate   = useNavigate();
  const { id }     = useParams();
  const isEditMode = Boolean(id);

  const { addProduct, updateProduct, products, returnPolicies } = useProducts();
  const { categories = [],    loading: categoriesLoading    } = useCategories();
  const { subCategories = [], loading: subCategoriesLoading } = useSubCategories();
  const { models }                                            = useModels();
  const { colors,    loading: colorsLoading  }               = useColors();
  const { sizes: productSizes, sizesLoading }                = useSizes();
  const { productTypes,   producttypeloading }               = useProductTypes();
  const { productMaterials, materialloading  }               = useProductMaterials();
  const { shapes, shapeloading }                             = useProductShapes();

  const [formData,    setFormData]    = useState(INITIAL_FORM);
  const [errors,      setErrors]      = useState({});
  const [isLoading,   setIsLoading]   = useState(false);
  const [isDragging,  setIsDragging]  = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [tagInput,    setTagInput]    = useState('');

  // ── FIX 1: Track whether we have successfully populated with full lookup data ──
  // We use a ref that stores the product id we last populated for.
  // We do NOT set it to true until all lookups are ready — this prevents the
  // "populate once with empty IDs, then never re-run" bug.
  const editPopulatedRef = useRef(false);

  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const videoSrc = useMemo(() => {
    if (!formData.video) return '';
    if (typeof formData.video === 'string') return formData.video;
    return URL.createObjectURL(formData.video);
  }, [formData.video]);

  // ── Edit-mode: populate form ─────────────────────────────────────────────────
  useEffect(() => {
    if (!isEditMode || !id) return;

    // Wait until the products list is loaded
    if (!products || products.length === 0) return;

    
    const lookupsReady =
      colors?.length > 0 &&
      productMaterials?.length > 0 &&
      productSizes?.length > 0 &&
      shapes?.length > 0;

    if (!lookupsReady) return;   

   
    if (editPopulatedRef.current) return;

    const product = products.find(p => String(p.id) === String(id));
    if (!product) { navigate('/products'); return; }

    // Mark as populated NOW (lookups are ready, product found)
    editPopulatedRef.current = true;

    // ── Product details ────────────────────────────────────────────────────
    const resolvedDetails =
      Array.isArray(product.productDetails) && product.productDetails.length > 0
        ? product.productDetails.map(d => ({
            title:   d.title   || '',
            content: d.content || '',
            image:   null,
            preview: d.preview || (d.image
              ? d.image.startsWith('http') ? d.image : `${CDN_URL}/${d.image}`
              : null),
          }))
        : parseProductDetails(product.description, CDN_URL);

    
    const variantsRaw = product.variations || product.variants || product.product_variations || [];
    const resolvedVariants = variantsRaw.map(v => ({
      uid:      crypto.randomUUID(),
      id:       v.id ? String(v.id) : null,
      title:    toStr(pick(v, ['title', 'name'])),
      image:    null,
      preview:  pick(v, ['preview', 'image_url', 'image'], null) || null,

      color:    resolveLookupId(pick(v, ['color_id', 'color']), pick(v, ['color_name', 'color']), colors, ['color_name']),
      material: resolveLookupId(pick(v, ['material_id', 'material']), pick(v, ['material_name', 'material']), productMaterials, ['name']),
      // Variant size DOES carry a real size_id from the API, so the
      // name-fallback resolver is fine here (unlike the product-level size).
      size:     resolveLookupId(pick(v, ['size_id', 'size']), pick(v, ['size_name', 'size']), productSizes, ['size', 'size_name']),
      shape:    resolveShapeId(v, shapes),

      price:    toStr(v.price),

      quantity: toStr(pick(v, ['quantity', 'stock'])),
      sku:      toStr(v.sku),
    }));

   
    const bulkOffersRaw = product.bulk_offers || product.bulkOffers || product.offers || [];
    const resolvedBulkOffers = bulkOffersRaw.map(o => ({
      uid:            crypto.randomUUID(),
      id:             o.id ? String(o.id) : null,
      min_quantity:   toStr(pick(o, ['min_quantity', 'minQuantity'])),
      max_quantity:   toStr(pick(o, ['max_quantity', 'maxQuantity'])),
      discount_type:  pick(o, ['discount_type', 'discountType'], 'fixed'),
      discount_value: toStr(pick(o, ['discount_value', 'discountValue'])),
      status:         pick(o, ['status'], 'Active'),
    }));

    // ── Effective sale_price ───────────────────────────────────────────────
    const yourPriceNum = parseFloat(String(product.your_price ?? 0));
    const effectiveSalePrice =
      !product.your_price || yourPriceNum === 0
        ? String(product.max_retail_price || '')
        : String(product.sale_price       || '');

    setFormData(prev => ({
      ...prev,
      product_name:             product.name                || '',
      brand_name:               product.brand_name          || '',
      model_name:               product.model_name          || '',
      manufacturer:             product.manufacturer        || '',
      item_type_name:           product.item_type_name      || '',
      style:                    product.style               || '',
      product_type:             product.product_type        ? String(product.product_type) : '',
      description:              product.description         || '',
      prod_description:         product.prod_description    || '',

      // FIX 3: resolve by id OR by name so these never render blank in edit mode
      color:      resolveLookupId(pick(product, ['color_id', 'color']), pick(product, ['color_name']), colors, ['color_name']),
      material:   resolveLookupId(pick(product, ['material_id', 'material']), pick(product, ['material_name']), productMaterials, ['name']),

      // ── FIX 4: product-level `size` is NOT reliably a foreign key ──────────
      // Real API payload example (product id 100):
      //   "size": "343*344"   ← free-text dimensions typed by a seller,
      //                          not one of the sizes-table ids (1, 2, 3…)
      // There is also no `size_name` field at the product level to fall back
      // on, unlike color/material. Feeding "343*344" into resolveLookupId's
      // name-matching path is guaranteed to fail (and is unsafe in general,
      // since it would try to match arbitrary text against the sizes list).
      // Use the strict resolver instead: only accept the value if it is
      // numeric AND actually exists in the loaded sizes lookup list.
      size: resolveStrictLookupId(pick(product, ['size_id', 'size']), productSizes),

      item_shape: resolveLookupId(pick(product, ['item_shape_id', 'item_shape']), pick(product, ['item_shape_name']), shapes, ['name']),

      frame_color:              product.frame_color         || '',
      frame_material:           product.frame_material      || '',
      frame_type:               product.frame_type          || '',
      is_customizable:          !!product.is_customizable,
      no_of_items:              product.no_of_items         || '1',

      your_price:               product.your_price                 ? String(product.your_price)              : '',
      max_retail_price:         product.max_retail_price           ? String(product.max_retail_price)         : '',
      min_seller_allowed_price: product.min_seller_allowed_price   ? String(product.min_seller_allowed_price) : '',
      max_seller_allowed_price: product.max_seller_allowed_price   ? String(product.max_seller_allowed_price) : '',
      sale_price:               effectiveSalePrice,
      prod_tax_code:            product.prod_tax_code              || '',
      sale_start_date:          product.sale_start_date            || '',
      sale_end_date:            product.sale_end_date              || '',
      offering_relase_date:     product.offering_relase_date       || '',
      merchant_release_date:    product.merchant_release_date      || '',
      max_order_quantity:       product.max_order_quantity         ? String(product.max_order_quantity) : '',
      min_order_qty:            product.min_order_qty              ? String(product.min_order_qty)      : '1',
      offering_message:         product.offering_message           || '',
      wrap_available:           !!product.wrap_available,

      sku:      product.sku               || '',
      quantity: product.stock != null     ? String(product.stock) : '',
      status:   product.status            || 'Active',

      item_length_longer_edge:  product.item_length_longer_edge || '',
      item_length_unit:         product.item_length_unit        || 'cm',
      item_width_shorter_edge:  product.item_width_shorter_edge || '',
      item_width_unit:          product.item_width_unit         || 'cm',
      item_depth:               product.item_depth              || '',
      item_weight:              product.item_weight             || '',
      item_weight_unit:         product.item_weight_unit        || 'kg',
      unit_count:               product.unit_count              || '',
      unit_count_type:          product.unit_count_type         || '',
      item_package_length:      product.item_package_length     || '',
      package_length_unit:      product.package_length_unit     || 'cm',
      item_package_width:       product.item_package_width      || '',
      package_width_unit:       product.package_width_unit      || 'cm',
      item_package_height:      product.item_package_height     || '',
      package_height_unit:      product.package_height_unit     || 'cm',
      item_package_weight:      product.item_package_weight     || '',
      package_weight_unit:      product.package_weight_unit     || 'kg',

      categoryId:      product.categoryId     || '',
      subCategoryId:   product.subCategoryId  || '',
      modalCategoryId: product.modalCategoryId || '',

      return_policy:  product.return_policy  || '',
      return_status:  product.return_status  || 'returnable',

      images: (product.images || []).map(url => ({ file: null, preview: url })),
      video:  product.video || '',

      productDetails: resolvedDetails,
      variants:       resolvedVariants,
      bulk_offers:    resolvedBulkOffers,

      tags: Array.isArray(product.tags)
        ? product.tags
        : product.tags
          ? String(product.tags).split(',').map(t => t.trim()).filter(Boolean)
          : [],

      meta_title:       product.meta_title       || '',
      meta_description: product.meta_description || '',
      slug:             product.slug             || '',
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, id, products, colors, productMaterials, productSizes, shapes]);

  // Reset populated flag when navigating to a different edit page
  useEffect(() => {
    editPopulatedRef.current = false;
  }, [id]);

  // ── Derived ─────────────────────────────────────────────────────────────────
  const filteredSubCategories = useMemo(
    () => subCategories.filter(s => s.category_id === formData.categoryId),
    [subCategories, formData.categoryId]
  );

  const filteredModalCategories = useMemo(() => {
    if (!Array.isArray(models) || !formData.subCategoryId) return [];
    return models.filter(
      m => String(m.sub_category_id) === String(formData.subCategoryId) && m.status === 'Active'
    );
  }, [models, formData.subCategoryId]);

  const hasModalCategories = filteredModalCategories.length > 0;
  const detailCount        = formData.productDetails.length;
  const isDetailLimitHit   = detailCount >= MAX_PRODUCT_DETAILS;
  const variantCount       = formData.variants.length;
  const isVariantLimitHit  = MAX_VARIANTS !== Infinity && variantCount >= MAX_VARIANTS;
  const bulkOfferCount     = formData.bulk_offers.length;
  const isBulkOfferLimitHit = MAX_BULK_OFFERS !== Infinity && bulkOfferCount >= MAX_BULK_OFFERS;

  const selectedCategory = useMemo(
    () => categories.find(c => String(c.id) === String(formData.categoryId)),
    [categories, formData.categoryId]
  );
  const isFrameCategory = useMemo(
    () => selectedCategory ? FRAME_KEYWORDS.test(selectedCategory.name) : false,
    [selectedCategory]
  );

  const discountPct = useMemo(() => {
    const mrp  = parseFloat(formData.max_retail_price);
    const sale = parseFloat(formData.sale_price);
    if (isNaN(mrp) || isNaN(sale) || mrp <= 0 || sale >= mrp) return null;
    return Math.round(((mrp - sale) / mrp) * 100);
  }, [formData.max_retail_price, formData.sale_price]);

  // ── Generic input handler ───────────────────────────────────────────────────
  const handleInputChange = useCallback(({ target: { name, value, type, checked } }) => {
    let cooked = value;
    if (type === 'checkbox') {
      cooked = checked;
    } else if (type === 'number') {
      cooked = preventNegativeInput(value);
    } else if (CAPITALIZE_FIELDS.has(name) && typeof value === 'string') {
      cooked = value.charAt(0).toUpperCase() + value.slice(1);
    }

    setFormData(prev => {
      const next = { ...prev, [name]: cooked };
      if (name === 'your_price') {
        const n = parseFloat(cooked);
        next.sale_price = (!cooked || n === 0) ? (prev.max_retail_price || '') : cooked;
      }
      if (name === 'max_retail_price' && (!prev.your_price || parseFloat(prev.your_price) === 0)) {
        next.sale_price = cooked;
      }
      if (name === 'categoryId')    { next.subCategoryId = ''; next.modalCategoryId = ''; }
      if (name === 'subCategoryId') { next.modalCategoryId = ''; }
      if (name === 'item_length_unit') {
        next.item_width_unit     = cooked;
        next.package_length_unit = cooked;
        next.package_width_unit  = cooked;
        next.package_height_unit = cooked;
      }
      if (name === 'item_weight_unit') next.package_weight_unit = cooked;
      if (name === 'product_name') {
        if (!prev.meta_title || prev.meta_title === prev.product_name)
          next.meta_title = cooked;
        if (!prev.slug || prev.slug === toSlug(prev.product_name))
          next.slug = toSlug(cooked);
      }
      return next;
    });

    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  }, [errors]);

  // ── Product Details handlers ─────────────────────────────────────────────────
  const addProductDetail = () => {
    if (isDetailLimitHit) return;
    setFormData(prev => ({ ...prev, productDetails: [...prev.productDetails, blankDetail()] }));
  };

  const removeProductDetail = idx =>
    setFormData(prev => {
      const updated = [...prev.productDetails];
      if (updated[idx]?.image instanceof File) URL.revokeObjectURL(updated[idx].preview);
      return { ...prev, productDetails: updated.filter((_, i) => i !== idx) };
    });

  const updateDetailField = (idx, field, value) =>
    setFormData(prev => {
      const updated = [...prev.productDetails];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...prev, productDetails: updated };
    });

  // FIX: validates size/type immediately instead of waiting for the backend
  const handleDetailImageUpload = (idx, file) => {
    if (!file) return;
    const validationError = validateImageFile(file);
    if (validationError) {
      setErrors(prev => ({ ...prev, [`detail_image_${idx}`]: validationError }));
      return;
    }
    setErrors(prev => ({ ...prev, [`detail_image_${idx}`]: '' }));
    const preview = URL.createObjectURL(file);
    setFormData(prev => {
      const updated = [...prev.productDetails];
      if (updated[idx]?.image instanceof File) URL.revokeObjectURL(updated[idx].preview);
      updated[idx] = { ...updated[idx], image: file, preview };
      return { ...prev, productDetails: updated };
    });
  };

  const removeDetailImage = idx =>
    setFormData(prev => {
      const updated = [...prev.productDetails];
      if (updated[idx]?.image instanceof File) URL.revokeObjectURL(updated[idx].preview);
      updated[idx] = { ...updated[idx], image: null, preview: null };
      return { ...prev, productDetails: updated };
    });

  // ── Variant handlers ─────────────────────────────────────────────────────────
  const addVariant = () => {
    if (isVariantLimitHit) return;
    setFormData(prev => ({ ...prev, variants: [...prev.variants, blankVariant()] }));
  };

  const removeVariant = idx =>
    setFormData(prev => {
      const updated = [...prev.variants];
      if (updated[idx]?.image instanceof File) URL.revokeObjectURL(updated[idx].preview);
      return { ...prev, variants: updated.filter((_, i) => i !== idx) };
    });

  const updateVariantField = (idx, field, value) =>
    setFormData(prev => {
      const updated = [...prev.variants];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...prev, variants: updated };
    });

  // FIX: validates size/type immediately instead of waiting for the backend
  const handleVariantImageUpload = (idx, file) => {
    if (!file) return;
    const validationError = validateImageFile(file);
    if (validationError) {
      setErrors(prev => ({ ...prev, [`variant_image_${idx}`]: validationError }));
      return;
    }
    setErrors(prev => ({ ...prev, [`variant_image_${idx}`]: '' }));
    const preview = URL.createObjectURL(file);
    setFormData(prev => {
      const updated = [...prev.variants];
      if (updated[idx]?.image instanceof File) URL.revokeObjectURL(updated[idx].preview);
      updated[idx] = { ...updated[idx], image: file, preview };
      return { ...prev, variants: updated };
    });
  };

  const removeVariantImage = idx =>
    setFormData(prev => {
      const updated = [...prev.variants];
      if (updated[idx]?.image instanceof File) URL.revokeObjectURL(updated[idx].preview);
      updated[idx] = { ...updated[idx], image: null, preview: null };
      return { ...prev, variants: updated };
    });

  // ── Bulk Offer handlers ──────────────────────────────────────────────────────
  const addBulkOffer = () => {
    if (isBulkOfferLimitHit) return;
    setFormData(prev => ({ ...prev, bulk_offers: [...prev.bulk_offers, blankBulkOffer()] }));
  };

  const removeBulkOffer = idx =>
    setFormData(prev => ({ ...prev, bulk_offers: prev.bulk_offers.filter((_, i) => i !== idx) }));

  const updateBulkOfferField = (idx, field, value) =>
    setFormData(prev => {
      const updated = [...prev.bulk_offers];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...prev, bulk_offers: updated };
    });

  // ── Tag handlers ────────────────────────────────────────────────────────────
  const addTag = (raw) => {
    const tag = raw.trim().replace(/,+$/, '').trim();
    if (!tag) return;
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag) ? prev.tags : [...prev.tags, tag],
    }));
    setTagInput('');
  };

  const removeTag = (tag) =>
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(tagInput); }
    if (e.key === 'Backspace' && !tagInput && formData.tags.length)
      removeTag(formData.tags[formData.tags.length - 1]);
  };

  // ── Images / Video handlers ───────────────────────────────────────────────────
  // FIX: every incoming file is validated (type + size) before it's added to
  // state. Files that fail are simply skipped and reported via `errors.images`
  // instead of being silently accepted and only failing at submit time.
  const handleImageUpload = files => {
    const incoming = Array.from(files).slice(0, 5 - formData.images.length);
    const accepted = [];
    let firstError = '';

    incoming.forEach(file => {
      const validationError = validateImageFile(file);
      if (validationError) {
        if (!firstError) firstError = `${file.name}: ${validationError}`;
      } else {
        accepted.push(file);
      }
    });

    setErrors(prev => ({ ...prev, images: firstError || (accepted.length ? '' : prev.images) }));
    if (!accepted.length) return;

    setFormData(prev => ({
      ...prev,
      images: [
        ...prev.images,
        ...accepted.map(file => ({ file, preview: URL.createObjectURL(file) })),
      ],
    }));
  };

  // FIX: adds a size check alongside the existing MP4-only check
  const handleVideoUpload = file => {
    if (!file) return;
    const validationError = validateVideoFile(file);
    if (validationError) {
      setErrors(prev => ({ ...prev, video: validationError }));
      return;
    }
    setErrors(prev => ({ ...prev, video: '' }));
    setFormData(prev => ({ ...prev, video: file }));
  };

  const removeImage = idx => {
    const removed = formData.images[idx];
    if (removed?.file) URL.revokeObjectURL(removed.preview);
    setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));
  };

  const removeVideo = () => setFormData(prev => ({ ...prev, video: '' }));

  const handleDragOver  = e => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = e => { e.preventDefault(); setIsDragging(false); };
  const handleDrop = e => {
    e.preventDefault(); setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    const imgs  = files.filter(f => f.type.startsWith('image/'));
    const vids  = files.filter(f => f.type.startsWith('video/'));
    if (imgs.length) handleImageUpload(imgs);
    if (vids.length && !formData.video) handleVideoUpload(vids[0]);
  };

  // ── Validation ──────────────────────────────────────────────────────────────
  const validateStep = (step) => {
    const e = {};

    if (step === 0) {
      if (!formData.product_name.trim()) e.product_name  = 'Product name is required';
      if (!formData.categoryId)          e.categoryId    = 'Category is required';
      if (!formData.subCategoryId)       e.subCategoryId = 'Sub-category is required';
      if (hasModalCategories && !formData.modalCategoryId)
        e.modalCategoryId = 'Modal category is required for this sub-category';
    }

    if (step === 1) {
      if (!isEditMode && !formData.images.some(img => img?.file instanceof File))
        e.images = 'At least one image is required';
    }

    if (step === 3) {
      if (formData.your_price === '' || formData.your_price === null)
        e.your_price = 'Valid price is required';
      if (formData.max_retail_price === '' || formData.max_retail_price === null)
        e.max_retail_price = 'MRP is required';
      if (!formData.return_policy && formData.return_status !== 'non_returnable')
        e.return_policy = 'Return policy is required';

      const mrp       = parseFloat(formData.max_retail_price);
      const yourPrice = parseFloat(formData.your_price);
      const salePrice = parseFloat(formData.sale_price);
      if (!isNaN(yourPrice) && !isNaN(mrp) && yourPrice > mrp)
        e.your_price = `Your price (₹${yourPrice}) cannot exceed MRP (₹${mrp})`;
      if (!isNaN(salePrice) && !isNaN(mrp) && salePrice > mrp)
        e.sale_price = `Sale price (₹${salePrice}) cannot exceed MRP (₹${mrp})`;

      const minOrd = parseFloat(formData.min_order_qty);
      const maxOrd = parseFloat(formData.max_order_quantity);
      if (formData.min_order_qty === '' || isNaN(minOrd)) {
        e.min_order_qty = 'Minimum order quantity is required';
      } else if (minOrd < 1) {
        e.min_order_qty = 'Minimum order quantity must be at least 1';
      }
      if (formData.max_order_quantity !== '' && !isNaN(maxOrd) && maxOrd <= minOrd)
        e.max_order_quantity = `Max order quantity (${maxOrd}) must be greater than min (${minOrd})`;

      const dimChecks = [
        ['item_package_length', 'item_length_longer_edge', 'item_length_unit', 'Package length must be ≥ product length'],
        ['item_package_width',  'item_width_shorter_edge', 'item_width_unit',  'Package width must be ≥ product width'],
        ['item_package_height', 'item_depth',              'item_length_unit', 'Package height must be ≥ product depth'],
        ['item_package_weight', 'item_weight',             'item_weight_unit', 'Package weight must be ≥ product weight'],
      ];
      dimChecks.forEach(([pkgF, prodF, unitF, msg]) => {
        const pkg  = parseFloat(formData[pkgF]);
        const prod = parseFloat(formData[prodF]);
        if (!isNaN(pkg) && !isNaN(prod) && pkg < prod)
          e[pkgF] = `${msg} (${prod} ${formData[unitF]})`;
      });

      // ── Bulk offer validation (optional tiers, but if added must be valid) ──
      formData.bulk_offers.forEach((offer, idx) => {
        const minQ = parseFloat(offer.min_quantity);
        const maxQ = parseFloat(offer.max_quantity);
        const val  = parseFloat(offer.discount_value);

        if (offer.min_quantity === '' || isNaN(minQ) || minQ < 1)
          e[`bulk_min_${idx}`] = 'Min quantity is required';
        if (offer.max_quantity !== '' && !isNaN(maxQ) && !isNaN(minQ) && maxQ <= minQ)
          e[`bulk_max_${idx}`] = 'Max quantity must be greater than min';
        if (offer.discount_value === '' || isNaN(val) || val <= 0)
          e[`bulk_value_${idx}`] = 'Discount value is required';
        if (offer.discount_type === 'percent' && !isNaN(val) && val > 100)
          e[`bulk_value_${idx}`] = 'Percent discount cannot exceed 100';
        if (offer.discount_type === 'fixed' && !isNaN(val) && !isNaN(yourPrice) && val >= yourPrice)
          e[`bulk_value_${idx}`] = 'Fixed discount must be less than the product price';
      });

      // Overlapping bulk tier ranges
      const sortedOffers = formData.bulk_offers
        .map((o, idx) => ({ ...o, idx }))
        .filter(o => o.min_quantity !== '' && !isNaN(parseFloat(o.min_quantity)))
        .sort((a, b) => parseFloat(a.min_quantity) - parseFloat(b.min_quantity));
      for (let i = 1; i < sortedOffers.length; i++) {
        const prevOffer = sortedOffers[i - 1];
        const curOffer  = sortedOffers[i];
        const prevMax = prevOffer.max_quantity !== '' ? parseFloat(prevOffer.max_quantity) : Infinity;
        if (parseFloat(curOffer.min_quantity) <= prevMax) {
          e[`bulk_min_${curOffer.idx}`] = 'Quantity range overlaps with another tier';
        }
      }
    }

    // ── FIX 2: Step 4 (Variants tab) validation ──────────────────────────────
    // Previously step 4 required at least one productDetail AND variant images,
    // which blocked "Next" from step 3→4 if those weren't filled yet (they're
    // on step 4 itself). Step 4 validation now only runs on final submit, not
    // on goNext(). The validateStep(4) used in validateForm() (submit) still
    // enforces all rules. goNext() from step 3 no longer calls validateStep(4).
    if (step === 4) {
      if (formData.productDetails.length === 0)
        e.productDetails = 'At least one product detail is required';

      formData.productDetails.forEach((detail, idx) => {
        if (!detail.image && !detail.preview)
          e[`detail_image_${idx}`] = 'Image is required for this detail';
      });

      formData.variants.forEach((v, idx) => {
        if (!v.title.trim())        e[`variant_title_${idx}`] = 'Title is required';
        if (!v.image && !v.preview) e[`variant_image_${idx}`] = 'Image is required';
        if (v.price === '' || v.price === null || isNaN(parseFloat(v.price)))
          e[`variant_price_${idx}`] = 'Price is required';
      });

      // Duplicate variant check (same color + size + material)
      const seen = new Map();
      formData.variants.forEach((v, idx) => {
        const key = `${v.color}|${v.material}|${v.size}`;
        if (key !== '||' && seen.has(key)) {
          e[`variant_title_${idx}`]           = 'Duplicate combination of colour, material & size';
          e[`variant_title_${seen.get(key)}`] = 'Duplicate combination of colour, material & size';
        } else {
          seen.set(key, idx);
        }
      });
    }

    return e;
  };

  const validateForm = () => {
    const allErrors = {};
    for (let s = 0; s < STEPS.length; s++) Object.assign(allErrors, validateStep(s));
    setErrors(allErrors);
    return Object.keys(allErrors).length === 0;
  };

  // ── FIX 2 CORE: goNext only validates the CURRENT step, never step 4 ────────
  // The old code called validateStep(currentStep) which was correct, BUT the
  // step pill buttons called setCurrentStep(i) directly, skipping validation.
  // The issue was that clicking "Next" on step 3 (Pricing) was correctly
  // validating step 3 — but if your_price / return_policy weren't filled,
  // it blocked navigation silently (errors set but page didn't scroll to them).
  // Now we scroll to the first error field automatically on failure.
  const goNext = (e) => {
    e?.stopPropagation();
  e?.preventDefault();
    const stepErrors = validateStep(currentStep);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(prev => ({ ...prev, ...stepErrors }));
      // Scroll to top so user sees the error banner / first error field
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setErrors({});
    setCurrentStep(s => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goPrev = () => {
    setCurrentStep(s => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── FIX 2: Step pill click — allow free navigation without re-validating ────
  // Previously clicking a completed step pill would silently re-run validation
  // and could scroll/confuse the user. Now pills navigate freely; validation
  // only runs on goNext() and final submit.
  const handleStepClick = (targetStep) => {
    setErrors({});
    setCurrentStep(targetStep);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Reset & Submit ──────────────────────────────────────────────────────────
  const resetForm = () => {
    setFormData(INITIAL_FORM);
    editPopulatedRef.current = false;
  };

  const handleSubmit = async e => {
     e.preventDefault();
  // Guard: only submit when on the last step
  if (currentStep < STEPS.length - 1) return;
  if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const fd = new FormData();
      const yourPriceIsZero    = !formData.your_price || parseFloat(formData.your_price) === 0;
      const effectiveYourPrice = yourPriceIsZero ? (formData.max_retail_price || '0') : formData.your_price;
      const effectiveSalePrice = yourPriceIsZero ? (formData.max_retail_price || '0') : formData.sale_price;

      fd.append('categoryId',    formData.categoryId);
      fd.append('subCategoryId', formData.subCategoryId);
      fd.append('product_name',  formData.product_name);
      fd.append('your_price',    effectiveYourPrice);
      fd.append('quantity',      formData.quantity !== '' && formData.quantity != null ? formData.quantity : 0);
      fd.append('status',        formData.status || 'Active');
      fd.append('min_order_qty', formData.min_order_qty);
      fd.append('return_status', formData.return_status);
      if (formData.modalCategoryId) fd.append('modalCategoryId', formData.modalCategoryId);

      OPTIONAL_FIELDS.forEach(field => {
        if (field === 'sale_price') {
          if (effectiveSalePrice !== '' && effectiveSalePrice != null)
            fd.append('sale_price', effectiveSalePrice);
          return;
        }
        if (formData[field] !== '' && formData[field] != null)
          fd.append(field, formData[field]);
      });

      fd.append('wrap_available',  formData.wrap_available  ? '1' : '0');
      fd.append('is_customizable', formData.is_customizable ? '1' : '0');
      fd.append('customisations',  formData.is_customizable ? 'Yes' : 'No');
      if (formData.tags.length) fd.append('tags', formData.tags.join(','));

      formData.images
        .filter(img => img?.file instanceof File)
        .forEach(img => fd.append('images[]', img.file));

      if (formData.video instanceof File) fd.append('video', formData.video);

      formData.productDetails.slice(0, MAX_PRODUCT_DETAILS).forEach((detail, idx) => {
        fd.append(`description[${idx}][title]`,   detail.title);
        fd.append(`description[${idx}][content]`, detail.content);
        if (detail.image instanceof File) {
          fd.append(`description[${idx}][image]`, detail.image);
        } else if (detail.preview) {
          fd.append(`description[${idx}][existing_image]`, detail.preview);
        }
      });

      formData.variants.forEach((v, idx) => {
        if (v.id) fd.append(`variations[${idx}][id]`, v.id);
        fd.append(`variations[${idx}][title]`, v.title);
        fd.append(`variations[${idx}][price]`, v.price);

        if (v.color) {
          fd.append(`variations[${idx}][color_id]`, v.color);
          fd.append(`variations[${idx}][color]`,    v.color);
        }
        if (v.material) {
          fd.append(`variations[${idx}][material_id]`, v.material);
          fd.append(`variations[${idx}][material]`,    v.material);
        }
        if (v.size) {
          fd.append(`variations[${idx}][size_id]`, v.size);
          fd.append(`variations[${idx}][size]`,    v.size);
        }
        if (v.shape) {
          fd.append(`variations[${idx}][shape_id]`,   v.shape);
          fd.append(`variations[${idx}][shape]`,      v.shape);
          fd.append(`variations[${idx}][item_shape]`, v.shape);
        }
        if (v.quantity !== '') fd.append(`variations[${idx}][quantity]`, v.quantity);
        if (v.sku)             fd.append(`variations[${idx}][sku]`,      v.sku);

        if (v.image instanceof File) {
          fd.append(`variations[${idx}][image]`, v.image);
        } else if (v.preview) {
          const filename = v.preview.startsWith('http')
            ? v.preview.split('/').pop()
            : v.preview;
          fd.append(`variations[${idx}][existing_image]`, filename);
        }
      });

      formData.bulk_offers.forEach((o, idx) => {
        if (o.id) fd.append(`bulk_offers[${idx}][id]`, o.id);
        fd.append(`bulk_offers[${idx}][min_quantity]`,   o.min_quantity);
        if (o.max_quantity !== '') fd.append(`bulk_offers[${idx}][max_quantity]`, o.max_quantity);
        fd.append(`bulk_offers[${idx}][discount_type]`,  o.discount_type);
        fd.append(`bulk_offers[${idx}][discount_value]`, o.discount_value);
        fd.append(`bulk_offers[${idx}][status]`,         o.status);
      });

      if (isEditMode) fd.append('_method', 'POST');

      const result = isEditMode ? await updateProduct(id, fd) : await addProduct(fd);
      if (result?.success && [200, 201].includes(result?.status)) {
        resetForm();
        navigate('/products');
        return;
      }

      // FIX: parse raw backend validation errors into friendly, field-level
      // messages instead of showing the raw column name to the user.
      const backendErrors = result?.errors || result?.data?.errors || result?.response?.data?.errors;
      if (backendErrors && typeof backendErrors === 'object') {
        setErrors(mapBackendErrors(backendErrors));
      } else {
        setErrors({ general: result?.message || 'Operation failed' });
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Submit error:', err);
      const backendErrors = err?.response?.data?.errors;
      if (backendErrors && typeof backendErrors === 'object') {
        setErrors(mapBackendErrors(backendErrors));
      } else {
        setErrors({ general: err?.response?.data?.message || 'Failed to save product' });
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsLoading(false);
    }
  };

  // ── Loading state ───────────────────────────────────────────────────────────
  const isPageLoading =
    categoriesLoading || subCategoriesLoading || colorsLoading || materialloading || sizesLoading ||
    (isEditMode && products.length === 0);

  if (isPageLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading…</p>
        </div>
      </div>
    );
  }

  // ── CSS class helpers ───────────────────────────────────────────────────────
  const inputCls = (err) =>
    `w-full px-4 py-3 bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 ${err ? 'border-red-300' : 'border-gray-200'}`;

  const numericInputCls = (err) =>
    `w-full px-4 py-3 bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 ${err ? 'border-red-300' : 'border-gray-200'} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`;

  const selectCls = 'w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400';

  // ── Step content ─────────────────────────────────────────────────────────────
  const renderStep = () => {
    switch (currentStep) {

      /* ──────────── STEP 0 · Basic ──────────── */
      case 0: return (
        <React.Fragment>
          <Section icon={<Tag className="w-6 h-6 text-teal-600" />} title="Categories">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <Label required>Category</Label>
                <select name="categoryId" value={formData.categoryId} onChange={handleInputChange} className={inputCls(errors.categoryId)}>
                  <option value="">Select a category…</option>
                  {categories.filter(c => ['Active', 1, '1'].includes(c.status)).map(c =>
                    <option key={c.id} value={c.id}>{c.name}</option>
                  )}
                </select>
                <FieldError msg={errors.categoryId} />
              </div>
              <div>
                <Label required>Sub-Category</Label>
                <select name="subCategoryId" value={formData.subCategoryId} onChange={handleInputChange}
                  disabled={!formData.categoryId} className={inputCls(errors.subCategoryId)}>
                  <option value="">Select a sub-category…</option>
                  {filteredSubCategories.map(s => <option key={s.id} value={s.id}>{s.subcat_name}</option>)}
                </select>
                <FieldError msg={errors.subCategoryId} />
              </div>
              <div>
                <Label required={hasModalCategories}>Modal Category</Label>
                <select name="modalCategoryId" value={formData.modalCategoryId} onChange={handleInputChange}
                  disabled={!formData.subCategoryId || !filteredModalCategories.length}
                  className={`${inputCls(errors.modalCategoryId)} ${!filteredModalCategories.length ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <option value="">{filteredModalCategories.length ? 'Select a model…' : 'No models available'}</option>
                  {filteredModalCategories.map(m => <option key={m.id} value={m.id}>{m.model_name}</option>)}
                </select>
                <FieldError msg={errors.modalCategoryId} />
              </div>
            </div>
          </Section>

          <Section icon={<Package className="w-6 h-6 text-teal-600" />} title="Basic Information">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="lg:col-span-2">
                <Label required>Product Name</Label>
                <input type="text" name="product_name" value={formData.product_name} onChange={handleInputChange}
                  placeholder="Enter a descriptive product name…" className={inputCls(errors.product_name)} />
                <FieldError msg={errors.product_name} />
              </div>
             
              <div className="lg:col-span-2">
                <Label> Description</Label>
                <textarea name="prod_description" value={formData.prod_description} onChange={handleInputChange}
                  placeholder="Enter a detailed product description…" rows={5}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 resize-none" />
              </div>
              {[
                ['product_type', 'Product Type', 'select'],
                ['brand_name',   'Brand Name',   'text',   'Enter brand name'],
                ['sku',          'SKU',          'text',   'e.g., PROD-001'],
                ['model_name',   'Model Name',   'text',   'Enter model name'],
                ['manufacturer', 'Manufacturer', 'text',   'Enter manufacturer name'],
                ['item_type_name','Item Type',   'text',   'e.g., Wall Art, Poster, Canvas'],
              ].map(([name, label, type, ph]) => (
                <div key={name}>
                  <Label>{label}</Label>
                  {type === 'select' ? (
                    <select name={name} value={formData[name]} onChange={handleInputChange} className={selectCls}>
                      <option value="">{producttypeloading ? 'Loading…' : 'Select product type'}</option>
                      {productTypes?.filter(p => p.status === 'Active').map(t =>
                        <option key={t.id} value={t.id}>{t.type_name}</option>
                      )}
                    </select>
                  ) : (
                    <input type="text" name={name} value={formData[name]} onChange={handleInputChange}
                      placeholder={ph} className={inputCls()} />
                  )}
                </div>
              ))}
            </div>
          </Section>

          <Section icon={<Tag className="w-6 h-6 text-teal-600" />} title="Tags / Keywords">
            <div>
              <Label>Tags</Label>
              <div className="flex flex-wrap gap-2 px-3 py-2 bg-white border border-gray-200 rounded-xl min-h-[48px] focus-within:ring-2 focus-within:ring-teal-400">
                {formData.tags.map(tag => (
                  <span key={tag} className="flex items-center gap-1 px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-sm font-medium">
                    {tag}
                    <button type="button" onClick={() => removeTag(tag)} className="hover:text-red-600"><X className="w-3 h-3" /></button>
                  </span>
                ))}
                <input type="text" value={tagInput} onChange={e => setTagInput(e.target.value)}
                  onKeyDown={handleTagKeyDown} onBlur={() => addTag(tagInput)}
                  placeholder={formData.tags.length ? '' : 'Type a tag and press Enter or comma…'}
                  className="flex-1 min-w-[160px] outline-none text-sm py-1" />
              </div>
              <p className="text-xs text-gray-400 mt-1">Press Enter or comma to add.</p>
            </div>
          </Section>
        </React.Fragment>
      );

      /* ──────────── STEP 1 · Images ──────────── */
      case 1: return (
        <Section icon={<ImageIcon className="w-6 h-6 text-teal-600" />} title="Images & Videos">
          <div className="mb-8">
            <Label required={!isEditMode}>Product Images (Max 5)</Label>
            {formData.images.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
                {formData.images.map((img, i) => (
                  <div key={i} className="relative group">
                    <img src={img.preview || img} alt={`Product ${i + 1}`}
                      className="w-full h-32 object-cover rounded-lg border border-gray-200" />
                    <button type="button" onClick={() => removeImage(i)}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity">
                      <X className="w-3 h-3" />
                    </button>
                    {i === 0 && <div className="absolute bottom-2 left-2 px-2 py-1 bg-teal-600 text-white text-xs rounded">Main</div>}
                  </div>
                ))}
              </div>
            )}
            {formData.images.length < 5 && (
              <div className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors
                ${isDragging ? 'border-teal-400 bg-teal-50' : 'border-gray-300 hover:border-teal-300'}
                ${errors.images ? 'border-red-300' : ''}`}
                onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
                <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-600 mb-3">Drag and drop images here, or click to browse</p>
                <button type="button" onClick={() => imageInputRef.current?.click()}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors">
                  <Upload className="w-5 h-5" /> Choose Images
                </button>
                <input ref={imageInputRef} type="file" accept="image/*" multiple className="hidden"
                  onChange={e => handleImageUpload(e.target.files)} />
                <p className="text-xs text-gray-500 mt-3">PNG, JPG, WebP up to {IMAGE_MAX_SIZE_MB} MB each. First image is the main image.</p>
              </div>
            )}
            <FieldError msg={errors.images} />
          </div>
          <div>
            <Label>Product Video (Optional)</Label>
            {formData.video ? (
              <div className="relative">
                <video src={videoSrc} className="w-full h-64 object-cover rounded-lg border border-gray-200" controls />
                <button type="button" onClick={removeVideo}
                  className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center">
                <Video className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-600 mb-3">Upload a product demonstration video</p>
                <button type="button" onClick={() => videoInputRef.current?.click()}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700">
                  <Upload className="w-5 h-5" /> Choose Video
                </button>
                <input ref={videoInputRef} type="file" accept="video/*" className="hidden"
                  onChange={e => handleVideoUpload(e.target.files[0])} />
                <p className="text-xs text-gray-500 mt-3">MP4 format, up to {VIDEO_MAX_SIZE_MB} MB</p>
              </div>
            )}
            <FieldError msg={errors.video} />
          </div>
        </Section>
      );

      /* ──────────── STEP 2 · Attributes ──────────── */
      case 2: return (
        <React.Fragment>
          <Section icon={<Palette className="w-6 h-6 text-teal-600" />} title="Product Attributes">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <Label>Material</Label>
                <select name="material" value={formData.material} onChange={handleInputChange} className={selectCls}>
                  <option value="">{materialloading ? 'Loading…' : 'Select material'}</option>
                  {productMaterials?.filter(m => m.status === 'Active').map(m =>
                    <option key={m.id} value={String(m.id)}>{m.name}</option>
                  )}
                </select>
              </div>
              <div>
                <Label>Color</Label>
                <select name="color" value={formData.color} onChange={handleInputChange}
                  disabled={colorsLoading} className={selectCls}>
                  <option value="">{colorsLoading ? 'Loading…' : 'Select color'}</option>
                  {colors?.filter(c => c.status === 'Active').map(c =>
                    <option key={c.id} value={String(c.id)}>{c.color_name}</option>
                  )}
                </select>
              </div>
              <div>
                <Label>Size</Label>
             <select name="size" value={formData.size} onChange={handleInputChange} className={selectCls}>
  <option value="">Select size</option>
  {productSizes?.filter(s => s.status === 'active').map(s =>
    <option key={s.id} value={String(s.id)}>{s.size || s.size_name}</option>
  )}
</select>
              </div>
              <div>
                <Label>Shape</Label>
              <select name="item_shape" value={formData.item_shape} onChange={handleInputChange} className={selectCls}>
  <option value="">{shapeloading ? 'Loading…' : 'Select shape'}</option>
  {shapes?.filter(s => s.status === 'Active').map(s => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
</select>
              </div>
              <div>
                <Label>Style</Label>
                <input type="text" name="style" value={formData.style} onChange={handleInputChange}
                  placeholder="e.g., Modern, Vintage, Abstract" className={inputCls()} />
              </div>
              <div>
                <Label>Number of Items</Label>
                <input type="number" name="no_of_items" value={formData.no_of_items} onChange={handleInputChange}
                  placeholder="1" min="0" className={numericInputCls()}
                  onWheel={handleNumericWheel} onKeyDown={handleNumericKeyDown} />
              </div>
            </div>
          </Section>

          {isFrameCategory && (
            <Section icon={<Layers className="w-6 h-6 text-teal-600" />} title="Frame & Art Specific">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  ['frame_color',    'Frame Color',    'e.g., Black, White, Gold'],
                  ['frame_material', 'Frame Material', 'e.g., Wood, Metal'],
                  ['frame_type',     'Frame Type',     'e.g., Standard, Gallery'],
                ].map(([name, label, ph]) => (
                  <div key={name}>
                    <Label>{label}</Label>
                    <input type="text" name={name} value={formData[name]} onChange={handleInputChange}
                      placeholder={ph} className={inputCls()} />
                  </div>
                ))}
              </div>
            </Section>
          )}
        </React.Fragment>
      );

      /* ──────────── STEP 3 · Pricing ──────────── */
      case 3: return (
        <React.Fragment>
          <Section icon={<IndianRupee className="w-6 h-6 text-teal-600" />} title="Pricing & Inventory">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <Label required>Your Price (₹)</Label>
                <input type="number" name="your_price" value={formData.your_price} onChange={handleInputChange}
                  placeholder="0.00" min="0" step="0.01" className={numericInputCls(errors.your_price)}
                  onWheel={handleNumericWheel} onKeyDown={handleNumericKeyDown} />
                <FieldError msg={errors.your_price} />
              </div>
              <div>
                <Label required>MRP (₹)</Label>
                <input type="number" name="max_retail_price" value={formData.max_retail_price} onChange={handleInputChange}
                  placeholder="0.00" min="0" step="0.01" className={numericInputCls(errors.max_retail_price)}
                  onWheel={handleNumericWheel} onKeyDown={handleNumericKeyDown} />
                <FieldError msg={errors.max_retail_price} />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <label className="block text-sm font-semibold text-gray-800">Sale Price (₹)</label>
                  {discountPct !== null && (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded-full">{discountPct}% off</span>
                  )}
                </div>
                <input type="number" name="sale_price" value={formData.sale_price} onChange={handleInputChange}
                  placeholder="0.00" min="0" step="0.01" className={numericInputCls(errors.sale_price)}
                  onWheel={handleNumericWheel} onKeyDown={handleNumericKeyDown} />
                <FieldError msg={errors.sale_price} />
              </div>
              {[
                ['min_seller_allowed_price', 'Min Seller Price (₹)'],
                ['max_seller_allowed_price', 'Max Seller Price (₹)'],
              ].map(([name, label]) => (
                <div key={name}>
                  <Label>{label}</Label>
                  <input type="number" name={name} value={formData[name]} onChange={handleInputChange}
                    placeholder="0.00" min="0" step="0.01" className={numericInputCls()}
                    onWheel={handleNumericWheel} onKeyDown={handleNumericKeyDown} />
                </div>
              ))}
              <div>
                <Label>Product Tax Code</Label>
                <input type="text" name="prod_tax_code" value={formData.prod_tax_code} onChange={handleInputChange}
                  placeholder="Tax classification code" className={inputCls()} />
              </div>
              {[
                ['sale_start_date',       'Sale Start Date'],
                ['sale_end_date',         'Sale End Date'],
                ['offering_relase_date',  'Offering Release Date'],
                ['merchant_release_date', 'Merchant Release Date'],
              ].map(([name, label]) => (
                <div key={name}>
                  <Label>{label}</Label>
                  <input type="date" name={name} value={formData[name]} onChange={handleInputChange} className={inputCls()} />
                </div>
              ))}
              <div>
                <Label>Stock Quantity</Label>
                <input type="number" name="quantity" value={formData.quantity} onChange={handleInputChange}
                  placeholder="0" min="0" className={numericInputCls()}
                  onWheel={handleNumericWheel} onKeyDown={handleNumericKeyDown} />
              </div>
              <div>
                <Label required>Return Policy</Label>
                <select name="return_policy"
                  value={formData.return_status === 'non_returnable' ? 'non_returnable' : formData.return_policy}
                  onChange={e => {
                    const val = e.target.value;
                    setFormData(prev => val === 'non_returnable'
                      ? { ...prev, return_status: 'non_returnable', return_policy: '' }
                      : { ...prev, return_status: 'returnable',     return_policy: val });
                  }} className={inputCls(errors.return_policy)}>
                  <option value="">Select return policy</option>
                  <option value="non_returnable">Non-Returnable</option>
                  {returnPolicies?.map(p => <option key={p.id} value={p.id}>{p.policy_name}</option>)}
                </select>
                <FieldError msg={errors.return_policy} />
              </div>
              <div>
                <Label required>Min Order Quantity</Label>
                <input type="number" name="min_order_qty" value={formData.min_order_qty} onChange={handleInputChange}
                  placeholder="1" min="0" className={numericInputCls(errors.min_order_qty)}
                  onWheel={handleNumericWheel} onKeyDown={handleNumericKeyDown} />
                <FieldError msg={errors.min_order_qty} />
              </div>
              <div>
                <Label>Max Order Quantity</Label>
                <input type="number" name="max_order_quantity" value={formData.max_order_quantity} onChange={handleInputChange}
                  placeholder="No limit" min="0" className={numericInputCls(errors.max_order_quantity)}
                  onWheel={handleNumericWheel} onKeyDown={handleNumericKeyDown} />
                {formData.min_order_qty !== '' && !errors.max_order_quantity && (
                  <p className="text-xs text-gray-400 mt-1">Must be greater than min ({formData.min_order_qty})</p>
                )}
                <FieldError msg={errors.max_order_quantity} />
              </div>
              <div>
                <Label>Product Status</Label>
                <div className="flex gap-4 mt-1">
                  {['Active', 'Inactive'].map(val => (
                    <label key={val} className="flex items-center cursor-pointer">
                      <input type="radio" name="status" value={val} checked={formData.status === val}
                        onChange={handleInputChange} className="sr-only" />
                      <div className={`relative w-6 h-6 rounded-full border-2 transition-all
                        ${formData.status === val
                          ? val === 'Active' ? 'border-green-500 bg-green-500' : 'border-gray-500 bg-gray-500'
                          : 'border-gray-300 bg-white'}`}>
                        {formData.status === val && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-2 h-2 bg-white rounded-full" />
                          </div>
                        )}
                      </div>
                      <span className="ml-3 text-sm font-medium text-gray-700">{val}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-3">
                <Label>Offering Message</Label>
                <textarea name="offering_message" value={formData.offering_message} onChange={handleInputChange}
                  placeholder="Special offering or promotional message…" rows={3}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 resize-none" />
              </div>
              <div className="flex items-center gap-6">
                {[['wrap_available','Gift Wrap Available'],['is_customizable','Customizable Product']].map(([name, label]) => (
                  <label key={name} className="flex items-center cursor-pointer">
                    <input type="checkbox" name={name} checked={formData[name]} onChange={handleInputChange} className="sr-only" />
                    <div className={`relative w-6 h-6 rounded border-2 transition-all ${formData[name] ? 'border-teal-500 bg-teal-500' : 'border-gray-300 bg-white'}`}>
                      {formData[name] && <div className="absolute inset-0 flex items-center justify-center"><div className="w-2 h-2 bg-white rounded-sm" /></div>}
                    </div>
                    <span className="ml-3 text-sm font-medium text-gray-700">{label}</span>
                  </label>
                ))}
              </div>
            </div>
          </Section>

          <Section
            icon={<Percent className="w-6 h-6 text-teal-600" />}
            title="Bulk Offers"
            headerRight={
              <button type="button" onClick={addBulkOffer} disabled={isBulkOfferLimitHit}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors
                  ${isBulkOfferLimitHit ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200' : 'bg-teal-600 text-white hover:bg-teal-700'}`}>
                {isBulkOfferLimitHit ? <Lock className="w-4 h-4" /> : <Plus className="w-4 h-4" />} Add Tier
              </button>
            }
          >
            {bulkOfferCount === 0 ? (
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-10 text-center">
                <Percent className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-400 text-sm">No bulk pricing tiers added yet.</p>
                <p className="text-gray-400 text-xs mt-1">Offer discounts to buyers who purchase in larger quantities.</p>
                <button type="button" onClick={addBulkOffer} className="mt-3 text-teal-600 text-sm font-medium hover:underline">+ Add your first tier</button>
              </div>
            ) : (
              <div className="space-y-4">
                {formData.bulk_offers.map((offer, idx) => (
                  <div key={offer.uid} className="border border-gray-200 rounded-xl p-5 bg-gray-50 bg-opacity-40">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-bold text-teal-700 bg-teal-50 px-3 py-1 rounded-full border border-teal-200">Tier {idx + 1}</span>
                      <button type="button" onClick={() => removeBulkOffer(idx)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-2">Min Qty <span className="text-red-500">*</span></label>
                        <input type="number" value={offer.min_quantity} min="1"
                          onChange={e => updateBulkOfferField(idx, 'min_quantity', preventNegativeInput(e.target.value))}
                          onWheel={handleNumericWheel} onKeyDown={handleNumericKeyDown} placeholder="e.g., 5"
                          className={`w-full px-4 py-3 bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${errors[`bulk_min_${idx}`] ? 'border-red-300' : 'border-gray-200'}`} />
                        <FieldError msg={errors[`bulk_min_${idx}`]} />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-2">Max Qty</label>
                        <input type="number" value={offer.max_quantity} min="0"
                          onChange={e => updateBulkOfferField(idx, 'max_quantity', preventNegativeInput(e.target.value))}
                          onWheel={handleNumericWheel} onKeyDown={handleNumericKeyDown} placeholder="No limit"
                          className={`w-full px-4 py-3 bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${errors[`bulk_max_${idx}`] ? 'border-red-300' : 'border-gray-200'}`} />
                        <FieldError msg={errors[`bulk_max_${idx}`]} />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-2">Discount Type</label>
                        <select value={offer.discount_type} onChange={e => updateBulkOfferField(idx, 'discount_type', e.target.value)}
                          className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400">
                          <option value="fixed">Fixed (₹)</option>
                          <option value="percent">Percent (%)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-2">
                          Discount Value {offer.discount_type === 'percent' ? '(%)' : '(₹)'} <span className="text-red-500">*</span>
                        </label>
                        <input type="number" value={offer.discount_value} min="0"
                          step={offer.discount_type === 'percent' ? '1' : '0.01'}
                          max={offer.discount_type === 'percent' ? '100' : undefined}
                          onChange={e => updateBulkOfferField(idx, 'discount_value', preventNegativeInput(e.target.value))}
                          onWheel={handleNumericWheel} onKeyDown={handleNumericKeyDown}
                          placeholder={offer.discount_type === 'percent' ? 'e.g., 10' : 'e.g., 100.00'}
                          className={`w-full px-4 py-3 bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${errors[`bulk_value_${idx}`] ? 'border-red-300' : 'border-gray-200'}`} />
                        <FieldError msg={errors[`bulk_value_${idx}`]} />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-2">Status</label>
                        <div className="flex gap-3 mt-1">
                          {['Active', 'Inactive'].map(val => (
                            <label key={val} className="flex items-center cursor-pointer">
                              <input type="radio" name={`bulk_status_${idx}`} value={val} checked={offer.status === val}
                                onChange={() => updateBulkOfferField(idx, 'status', val)} className="sr-only" />
                              <div className={`relative w-5 h-5 rounded-full border-2 transition-all
                                ${offer.status === val
                                  ? val === 'Active' ? 'border-green-500 bg-green-500' : 'border-gray-500 bg-gray-500'
                                  : 'border-gray-300 bg-white'}`}>
                                {offer.status === val && (
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-1.5 h-1.5 bg-white rounded-full" />
                                  </div>
                                )}
                              </div>
                              <span className="ml-2 text-xs font-medium text-gray-700">{val}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {!isBulkOfferLimitHit && (
                  <button type="button" onClick={addBulkOffer}
                    className="w-full py-3 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-500 hover:border-teal-300 hover:text-teal-600 hover:bg-teal-50 transition-colors flex items-center justify-center gap-2">
                    <Plus className="w-4 h-4" /> Add Another Tier
                  </button>
                )}
              </div>
            )}
          </Section>

          <Section icon={<Ruler className="w-6 h-6 text-teal-600" />} title="Physical Dimensions">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4">Product Dimensions</h3>
                <div className="space-y-4">
                  <DimRow label="Length (Longer Edge)" valName="item_length_longer_edge" unitName="item_length_unit"
                    formData={formData} onChange={handleInputChange} />
                  <DimRow label="Width (Shorter Edge)" valName="item_width_shorter_edge" unitName="item_width_unit"
                    formData={formData} onChange={handleInputChange} units={['cm','inches','mm']} />
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Depth / Thickness</label>
                      <input type="number" name="item_depth" value={formData.item_depth} onChange={handleInputChange}
                        placeholder="0.0" min="0" step="0.1"
                        className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        onWheel={handleNumericWheel} onKeyDown={handleNumericKeyDown} />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Weight</label>
                      <div className="grid grid-cols-2 gap-2">
                        <input type="number" name="item_weight" value={formData.item_weight} onChange={handleInputChange}
                          placeholder="0.0" min="0" step="0.01"
                          className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          onWheel={handleNumericWheel} onKeyDown={handleNumericKeyDown} />
                        <select name="item_weight_unit" value={formData.item_weight_unit} onChange={handleInputChange}
                          className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400">
                          {['kg','g','lbs'].map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Unit Count</label>
                      <input type="number" name="unit_count" value={formData.unit_count} onChange={handleInputChange}
                        placeholder="1" min="0"
                        className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        onWheel={handleNumericWheel} onKeyDown={handleNumericKeyDown} />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Unit Count Type</label>
                      <input type="text" name="unit_count_type" value={formData.unit_count_type} onChange={handleInputChange}
                        placeholder="pieces, sets…"
                        className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400" />
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4">Package Dimensions</h3>
                <div className="space-y-4">
                  {[
                    ['item_package_length', 'package_length_unit', 'Package Length'],
                    ['item_package_width',  'package_width_unit',  'Package Width'],
                    ['item_package_height', 'package_height_unit', 'Package Height'],
                  ].map(([valName, unitName, label]) => (
                    <DimRow key={valName} label={label} valName={valName} unitName={unitName}
                      formData={formData} onChange={handleInputChange} error={errors[valName]} />
                  ))}
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">Package Weight</label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <input type="number" name="item_package_weight" value={formData.item_package_weight} onChange={handleInputChange}
                          placeholder="0.0" min="0" step="0.01"
                          className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          onWheel={handleNumericWheel} onKeyDown={handleNumericKeyDown} />
                        <FieldError msg={errors.item_package_weight} />
                      </div>
                      <select name="package_weight_unit" value={formData.package_weight_unit} onChange={handleInputChange}
                        className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400">
                        {['kg','g','lbs'].map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Section>
        </React.Fragment>
      );

      /* ──────────── STEP 4 · Variants ──────────── */
      case 4: return (
        <React.Fragment>
          <Section
            icon={<ImageIcon className="w-6 h-6 text-teal-600" />}
            title="Product Details"
            headerRight={
              <div className="relative group">
                <button type="button" onClick={addProductDetail} disabled={isDetailLimitHit}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors
                    ${isDetailLimitHit ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200' : 'bg-teal-600 text-white hover:bg-teal-700'}`}>
                  {isDetailLimitHit ? <Lock className="w-4 h-4" /> : <Plus className="w-4 h-4" />} Add Detail
                </button>
                {isDetailLimitHit && (
                  <div className="absolute right-0 top-full mt-2 z-10 w-56 bg-gray-800 text-white text-xs rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity shadow-lg">
                    Maximum of {MAX_PRODUCT_DETAILS} product details allowed.
                  </div>
                )}
              </div>
            }
          >
            <div className="flex items-center justify-between -mt-2 mb-4">
              <p className="text-sm text-gray-500">
                {detailCount} / {MAX_PRODUCT_DETAILS} details added
                {isDetailLimitHit && <span className="ml-2 inline-flex items-center gap-1 text-xs font-medium text-red-500"><Lock className="w-3 h-3" /> Limit reached</span>}
              </p>
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  {Array.from({ length: MAX_PRODUCT_DETAILS }).map((_, i) => (
                    <div key={i} className={`h-1.5 w-10 rounded-full transition-colors ${i < detailCount ? 'bg-teal-500' : 'bg-gray-200'}`} />
                  ))}
                </div>
                <span className="text-xs text-gray-400">{MAX_PRODUCT_DETAILS - detailCount} slot{MAX_PRODUCT_DETAILS - detailCount !== 1 ? 's' : ''} remaining</span>
              </div>
            </div>

            {detailCount === 0 ? (
              <div className={`border-2 border-dashed rounded-xl p-10 text-center ${errors.productDetails ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}>
                <ImageIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-400 text-sm">No product details added yet.</p>
                {errors.productDetails && <p className="text-red-500 text-sm mt-1">{errors.productDetails}</p>}
                <button type="button" onClick={addProductDetail} className="mt-3 text-teal-600 text-sm font-medium hover:underline">+ Add your first detail</button>
              </div>
            ) : (
              <div className="space-y-6">
                {formData.productDetails.map((detail, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-xl p-6 bg-gray-50 bg-opacity-40">
                    <div className="flex items-center justify-between mb-5">
                      <span className="text-sm font-bold text-teal-700 bg-teal-50 px-3 py-1 rounded-full border border-teal-200">Detail {idx + 1}</span>
                      <button type="button" onClick={() => removeProductDetail(idx)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-800 mb-2">Title <span className="text-red-500">*</span></label>
                          <input type="text" value={detail.title} maxLength={100} onChange={e => updateDetailField(idx, 'title', e.target.value)}
                            placeholder="e.g., Polaroid Style Glossy Photo Prints"
                            className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400" />
                          <div className="flex justify-end mt-1"><span className="text-xs text-gray-400">{detail.title.length}/100</span></div>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-800 mb-2">Description <span className="text-red-500">*</span></label>
                          <textarea value={detail.content} maxLength={500} rows={6} onChange={e => updateDetailField(idx, 'content', e.target.value)}
                            placeholder="Describe this feature in detail…"
                            className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 resize-none" />
                          <div className="flex justify-end mt-1"><span className="text-xs text-gray-400">{detail.content.length}/500</span></div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-2">Image <span className="text-red-500">*</span></label>
                        {detail.preview ? (
                          <div className="relative group">
                            <img src={detail.preview} alt={`Detail ${idx + 1}`} className="w-full h-56 object-cover rounded-xl border border-gray-100" />
                            <button type="button" onClick={() => removeDetailImage(idx)}
                              className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                            <label className="absolute bottom-2 right-2 cursor-pointer">
                              <span className="px-3 py-1.5 bg-white bg-opacity-90 text-teal-700 text-xs font-medium rounded-lg border border-teal-200 hover:bg-teal-50 opacity-0 group-hover:opacity-100 inline-block transition-colors">Replace</span>
                              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={e => { if (e.target.files[0]) handleDetailImageUpload(idx, e.target.files[0]); }} />
                            </label>
                          </div>
                        ) : (
                          <label className="block cursor-pointer">
                            <div className={`border-2 border-dashed rounded-xl p-8 text-center hover:border-teal-300 hover:bg-teal-50 transition-colors h-56 flex flex-col items-center justify-center ${errors[`detail_image_${idx}`] ? 'border-red-300' : 'border-gray-200'}`}>
                              <Upload className="w-8 h-8 text-gray-300 mb-2" />
                              <p className="text-sm text-gray-500"><span className="text-teal-600 font-medium">Click to upload</span> or drag & drop</p>
                              <p className="text-xs text-gray-400 mt-1">PNG, JPG, WebP — max {IMAGE_MAX_SIZE_MB} MB</p>
                            </div>
                            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={e => { if (e.target.files[0]) handleDetailImageUpload(idx, e.target.files[0]); }} />
                          </label>
                        )}
                        {errors[`detail_image_${idx}`] && <p className="text-red-500 text-sm mt-1">{errors[`detail_image_${idx}`]}</p>}
                      </div>
                    </div>
                  </div>
                ))}
                {!isDetailLimitHit && (
                  <button type="button" onClick={addProductDetail}
                    className="w-full py-3 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-500 hover:border-teal-300 hover:text-teal-600 hover:bg-teal-50 transition-colors flex items-center justify-center gap-2">
                    <Plus className="w-4 h-4" /> Add Another Detail
                  </button>
                )}
              </div>
            )}
          </Section>

          <Section
            icon={<Boxes className="w-6 h-6 text-teal-600" />}
            title="Product Variants"
            headerRight={
              <button type="button" onClick={addVariant} disabled={isVariantLimitHit}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors
                  ${isVariantLimitHit ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200' : 'bg-teal-600 text-white hover:bg-teal-700'}`}>
                {isVariantLimitHit ? <Lock className="w-4 h-4" /> : <Plus className="w-4 h-4" />} Add Variant
              </button>
            }
          >
            {variantCount === 0 ? (
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-10 text-center">
                <Boxes className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-400 text-sm">No variants added yet.</p>
                <p className="text-gray-400 text-xs mt-1">Add variants for different colours, materials, sizes, shapes, or prices.</p>
                <button type="button" onClick={addVariant} className="mt-3 text-teal-600 text-sm font-medium hover:underline">+ Add your first variant</button>
              </div>
            ) : (
              <div className="space-y-6">
                {formData.variants.map((variant, idx) => (
                  <div key={variant.uid} className="border border-gray-200 rounded-xl p-6 bg-gray-50 bg-opacity-40">
                    <div className="flex items-center justify-between mb-5">
                      <span className="text-sm font-bold text-teal-700 bg-teal-50 px-3 py-1 rounded-full border border-teal-200">Variant {idx + 1}</span>
                      <button type="button" onClick={() => removeVariant(idx)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-800 mb-2">Title <span className="text-red-500">*</span></label>
                          <input type="text" value={variant.title} maxLength={100} onChange={e => updateVariantField(idx, 'title', e.target.value)}
                            placeholder="e.g., Red Canvas Large"
                            className={`w-full px-4 py-3 bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 ${errors[`variant_title_${idx}`] ? 'border-red-300' : 'border-gray-200'}`} />
                          <div className="flex items-center justify-between mt-1">
                            <FieldError msg={errors[`variant_title_${idx}`]} />
                            <span className="text-xs text-gray-400 ml-auto">{variant.title.length}/100</span>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-semibold text-gray-800 mb-2">Colour</label>
                            <select value={variant.color} onChange={e => updateVariantField(idx, 'color', e.target.value)} disabled={colorsLoading}
                              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400">
                              <option value="">{colorsLoading ? 'Loading…' : 'Select colour'}</option>
                              {colors?.filter(c => c.status === 'Active').map(c => <option key={c.id} value={String(c.id)}>{c.color_name}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-800 mb-2">Material</label>
                            <select value={variant.material} onChange={e => updateVariantField(idx, 'material', e.target.value)} disabled={materialloading}
                              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400">
                              <option value="">{materialloading ? 'Loading…' : 'Select material'}</option>
                              {productMaterials?.filter(m => m.status === 'Active').map(m => <option key={m.id} value={String(m.id)}>{m.name}</option>)}
                            </select>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-semibold text-gray-800 mb-2">Price (₹) <span className="text-red-500">*</span></label>
                            <input type="number" value={variant.price} min="0" step="0.01"
                              onChange={e => updateVariantField(idx, 'price', preventNegativeInput(e.target.value))}
                              onWheel={handleNumericWheel} onKeyDown={handleNumericKeyDown} placeholder="0.00"
                              className={`w-full px-4 py-3 bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${errors[`variant_price_${idx}`] ? 'border-red-300' : 'border-gray-200'}`} />
                            <FieldError msg={errors[`variant_price_${idx}`]} />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-800 mb-2">Size</label>
                          <select value={variant.size} onChange={e => updateVariantField(idx, 'size', e.target.value)}
  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400">
  <option value="">Select size</option>
  {productSizes?.filter(s => s.status === 'active').map(s =>
    <option key={s.id} value={String(s.id)}>{s.size || s.size_name}</option>
  )}
</select>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-800 mb-2">Shape</label>
                          <select value={variant.shape} onChange={e => updateVariantField(idx, 'shape', e.target.value)} disabled={shapeloading}
                            className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400">
                            <option value="">{shapeloading ? 'Loading…' : 'Select shape'}</option>
                            {shapes?.filter(s => s.status === 'Active').map(s => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
                          </select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-semibold text-gray-800 mb-2">Variant SKU</label>
                            <input type="text" value={variant.sku} onChange={e => updateVariantField(idx, 'sku', e.target.value)}
                              placeholder="e.g., RED-LG-001"
                              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400" />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-800 mb-2">Stock Qty</label>
                            <input type="number" value={variant.quantity} min="0"
                              onChange={e => updateVariantField(idx, 'quantity', preventNegativeInput(e.target.value))}
                              onWheel={handleNumericWheel} onKeyDown={handleNumericKeyDown} placeholder="0"
                              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-2">Main Image <span className="text-red-500">*</span></label>
                        {variant.preview ? (
                          <div className="relative group">
                            <img src={variant.preview} alt={`Variant ${idx + 1}`} className="w-full h-56 object-cover rounded-xl border border-gray-100" />
                            <button type="button" onClick={() => removeVariantImage(idx)}
                              className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                            <label className="absolute bottom-2 right-2 cursor-pointer">
                              <span className="px-3 py-1.5 bg-white bg-opacity-90 text-teal-700 text-xs font-medium rounded-lg border border-teal-200 hover:bg-teal-50 opacity-0 group-hover:opacity-100 inline-block transition-colors">Replace</span>
                              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={e => { if (e.target.files[0]) handleVariantImageUpload(idx, e.target.files[0]); }} />
                            </label>
                          </div>
                        ) : (
                          <label className="block cursor-pointer">
                            <div className={`border-2 border-dashed rounded-xl p-8 text-center hover:border-teal-300 hover:bg-teal-50 transition-colors h-56 flex flex-col items-center justify-center ${errors[`variant_image_${idx}`] ? 'border-red-300' : 'border-gray-200'}`}>
                              <Upload className="w-8 h-8 text-gray-300 mb-2" />
                              <p className="text-sm text-gray-500"><span className="text-teal-600 font-medium">Click to upload</span> or drag & drop</p>
                              <p className="text-xs text-gray-400 mt-1">PNG, JPG, WebP — max {IMAGE_MAX_SIZE_MB} MB</p>
                            </div>
                            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={e => { if (e.target.files[0]) handleVariantImageUpload(idx, e.target.files[0]); }} />
                          </label>
                        )}
                        {errors[`variant_image_${idx}`] && <p className="text-red-500 text-sm mt-1">{errors[`variant_image_${idx}`]}</p>}
                      </div>
                    </div>
                  </div>
                ))}
                {!isVariantLimitHit && (
                  <button type="button" onClick={addVariant}
                    className="w-full py-3 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-500 hover:border-teal-300 hover:text-teal-600 hover:bg-teal-50 transition-colors flex items-center justify-center gap-2">
                    <Plus className="w-4 h-4" /> Add Another Variant
                  </button>
                )}
              </div>
            )}
          </Section>

          <Section icon={<Search className="w-6 h-6 text-teal-600" />} title="SEO">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Meta Title</Label>
                <input type="text" name="meta_title" value={formData.meta_title} onChange={handleInputChange}
                  placeholder="Auto-filled from product name" maxLength={60} className={inputCls()} />
                <p className="text-xs text-gray-400 mt-1">{formData.meta_title.length}/60 chars</p>
              </div>
              <div>
                <Label>URL Slug</Label>
                <input type="text" name="slug" value={formData.slug} onChange={handleInputChange}
                  placeholder="auto-generated-from-name" className={inputCls()} />
                <p className="text-xs text-gray-400 mt-1">Used in the product URL</p>
              </div>
              <div className="md:col-span-2">
                <Label>Meta Description</Label>
                <textarea name="meta_description" value={formData.meta_description} onChange={handleInputChange}
                  placeholder="Brief description shown in search results…" rows={3} maxLength={160}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 resize-none" />
                <p className="text-xs text-gray-400 mt-1">{formData.meta_description.length}/160 chars</p>
              </div>
            </div>
          </Section>
        </React.Fragment>
      );

      default: return null;
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 bg-opacity-90">
      <div className="bg-white/80 backdrop-blur-lg border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Link to="/products" className="flex items-center gap-2 text-gray-600 hover:text-teal-600 transition-colors">
                <ArrowLeft className="w-5 h-5" /><span>Back</span>
              </Link>
              <div className="w-px h-6 bg-gray-300" />
              <h1 className="text-xl font-bold text-teal-800">
                {isEditMode ? 'Edit Product' : 'Add New Product'}
              </h1>
            </div>
            <button type="button" onClick={() => navigate('/products')} disabled={isLoading}
              className="px-4 py-1.5 text-sm bg-white text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50">
              Cancel
            </button>
          </div>
          {/* ── FIX 2: Step pills use handleStepClick instead of setCurrentStep directly ── */}
          <div className="flex items-center gap-0">
            {STEPS.map((label, i) => (
              <React.Fragment key={label}>
                <button type="button" onClick={() => handleStepClick(i)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors
                    ${i === currentStep ? 'bg-teal-600 text-white' : i < currentStep ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-500'}`}>
                  {i < currentStep ? <Check className="w-3 h-3" /> : <span>{i + 1}</span>}
                  {label}
                </button>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-1 ${i < currentStep ? 'bg-teal-400' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        <form id="add-product-form" onSubmit={handleSubmit} className="space-y-8">
          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
              {errors.general}
            </div>
          )}
          {renderStep()}
          <div className="flex justify-between gap-4 pt-8">
            <button type="button" onClick={goPrev} disabled={currentStep === 0}
              className="flex items-center gap-2 px-6 py-3 bg-white border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed font-medium">
              <ChevronLeft className="w-5 h-5" /> Previous
            </button>
            {currentStep < STEPS.length - 1 ? (
              <button type="button" onClick={(e) => goNext(e)}
                className="flex items-center gap-2 px-8 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 font-medium">
                Next <ChevronRight className="w-5 h-5" />
              </button>
            ) : (
              <button type="submit" disabled={isLoading}
                className="flex items-center gap-2 px-8 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 disabled:opacity-50 font-medium">
                {isLoading
                  ? <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />{isEditMode ? 'Updating…' : 'Saving…'}</>
                  : <><Save className="w-5 h-5" />{isEditMode ? 'Update Product' : 'Save Product'}</>}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddProductPage;

// ── Small reusable sub-components ─────────────────────────────────────────────

const Section = ({ icon, title, children, headerRight }) => (
  <div className="bg-white/80 rounded-xl border border-gray-200 p-8">
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center gap-3">
        {icon}
        <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
      </div>
      {headerRight}
    </div>
    {children}
  </div>
);

const Label = ({ children, required }) => (
  <label className="block text-sm font-semibold text-gray-800 mb-3">
    {children}{required && <span className="text-red-500 ml-0.5">*</span>}
  </label>
);

const FieldError = ({ msg }) =>
  msg ? <p className="text-red-500 text-sm mt-1">{msg}</p> : null;

const DimRow = ({ label, valName, unitName, formData, onChange, error, units = ['cm', 'inches', 'mm'] }) => (
  <div className="grid grid-cols-2 gap-4">
    <div>
      <label className="block text-sm font-semibold text-gray-800 mb-2">{label}</label>
      <input type="number" name={valName} value={formData[valName]} onChange={onChange}
        placeholder="0.0" min="0" step="0.1"
        className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        onWheel={handleNumericWheel} onKeyDown={handleNumericKeyDown} />
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
    <div>
      <label className="block text-sm font-semibold text-gray-800 mb-2">Unit</label>
      <select name={unitName} value={formData[unitName]} onChange={onChange}
        className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400">
        {units.map(u => <option key={u} value={u}>{u}</option>)}
      </select>
    </div>
  </div>
);